import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';

/** Step one of sign-in: what the browser needs to hash locally. */
export interface LoginChallengeResponse {
  /** Public prefix of the stored hash — algorithm, cost and salt. */
  salt: string;
  /** Makes the resulting proof usable exactly once. */
  nonce: string;
  challengeId: string;
}

export type LoginRoute =
  // No `jwt` while the T&C are pending — sso-be mints the token only after acceptance.
  | { kind: 'SINGLE_LAB'; labId: string; labUrl: string; jwt?: string }
  | { kind: 'AUTO_ROUTE'; labId: string; labUrl: string; jwt: string }
  | {
      kind: 'PICKER';
      labs: Array<{ labId: string; labUrl: string; lastLoginAt: string | null }>;
    };

/** Which second factors the account holds, for the challenge screen to offer. */
export interface MfaMethods {
  totp: boolean;
  email: boolean;
  backup: boolean;
}

export type MfaMethod = 'TOTP' | 'EMAIL' | 'BACKUP';

/**
 * What came of asking for an emailed code.
 *
 * `SENT` is the only one of the three where mail is actually on its way.
 * The other two exist because the screen used to be told `SENT` regardless,
 * so it announced codes that were never sent and started countdowns for them.
 *
 * `cooldownSec` is the wait before another request is worth making — the time
 * remaining on the current cooldown for `COOLDOWN`, and zero for `SEND_LIMIT`,
 * where no amount of waiting helps.
 */
export interface MfaSendResult {
  status: 'SENT' | 'COOLDOWN' | 'SEND_LIMIT';
  cooldownSec: number;
}

/**
 * Returned when the password was right but a second factor is enrolled.
 *
 * Carries no challenge identifier — that rides in an HttpOnly cookie the
 * browser sends back automatically, so it survives a page refresh and stays
 * out of reach of scripts.
 */
export interface MfaRequiredResponse {
  status: 'MFA_REQUIRED';
  methods: MfaMethods;
  preferred: 'TOTP' | 'EMAIL';
  emailHint?: string;
}

/** Too many failed attempts. Only returned once the password is correct. */
export interface AccountLockedResponse {
  status: 'ACCOUNT_LOCKED';
  lockedForMinutes: number;
  message: string;
}

export type LoginResponse =
  | {
      status: 'AUTH_OK';
      route: LoginRoute;
      /**
       * When this identity accepted the top-level Terms & Conditions, or
       * null when it has not yet. The UI opens the T&C dialog for a null
       * value and blocks the trip to a workspace until the user accepts —
       * even the SINGLE_LAB and AUTO_ROUTE branches, which would otherwise
       * silently hand them in without the question.
       *
       * ISO date string over the wire; the field only ever needs to be
       * truthy-checked, so the exact format is not depended on anywhere.
       */
      termsAcceptedAt: string | null;
    }
  | { status: 'SETUP_REQUIRED' }
  | { status: 'RESET_REQUIRED' }
  | AccountLockedResponse
  | MfaRequiredResponse;

export interface MfaContextResponse {
  methods: MfaMethods;
  preferred: 'TOTP' | 'EMAIL';
  emailHint?: string;
  expiresAt: string;
}

export interface MfaVerifyResponse {
  status: 'AUTH_OK';
  route: LoginRoute;
  /** Same meaning as on `LoginResponse.AUTH_OK`; see above. */
  termsAcceptedAt: string | null;
  notice?: { kind: 'BACKUP_CODE_USED'; backupCodesRemaining: number };
}

export interface MfaStatusResponse {
  totp: { enabled: boolean };
  email: { enabled: boolean; hint?: string };
  backupCodesRemaining: number;
  enrolledAt: string | null;
  /**
   * Seconds left in which this session may turn a factor off or reissue
   * recovery codes. Zero once it has to prove itself again.
   *
   * Counted from when the session began rather than from the last request, so
   * it runs down while the page sits open and is not refreshed by using it.
   */
  modifyFreshForSec: number;
}

export interface TotpStartResponse {
  otpauthUri: string;
  manualEntryKey: string;
}

/** Enrolment steps report why they failed so the screen can say something useful. */
export type EnrolResponse =
  | { status: 'OK'; backupCodes?: string[] }
  | { status: 'INVALID_CODE' }
  | { status: 'NOT_STARTED' };

export interface SwitchResponse {
  status: 'AUTH_OK';
  jwt: string;
  target: string;
  labUrl: string;
}

export type TokenResult = { status: 'OK' } | { status: 'INVALID' };

/**
 * What comes back from spending a Lab's handoff ticket.
 *
 * It names the workspace that vouched, which is how the security pages know
 * where to offer a way back. The Lab also puts its own address in an `rd`
 * query parameter, but it has to guess its public hostname from proxy headers
 * to do so — behind a load balancer that guess is often an internal name
 * matching no registered workspace, and the way back silently disappears.
 * This answer is derived server-side from the ticket itself.
 */
export interface RedeemSessionTicketResponse {
  status: 'OK';
  labId: string;
  /** Absent when the workspace is registered without a URL — a config gap. */
  labUrl?: string;
}

export interface MyLabsResponse {
  labs: Array<{
    labId: string;
    labUrl: string;
    lastLoginAt: string | null;
    /** Where entering lands the browser, when that is not the Lab itself. */
    devRedirectUrl?: string;
  }>;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly baseUrl = environment.ssoBaseUrl.replace(/\/$/, '');
  private readonly withCreds = { withCredentials: true };

  constructor(private http: HttpClient) {}

  /**
   * Request options carrying a solved captcha, where there is one.
   *
   * Sent as a header rather than a body field so the request DTOs stay about
   * what the user typed, and so every guarded endpoint looks the same to the
   * server — the same reasoning as the CSRF header alongside it.
   *
   * A missing token is simply omitted. It is not the frontend's place to
   * decide whether that is acceptable: the server knows whether it holds keys
   * and whether it is armed yet, and it is the only side that can log the
   * decision.
   */
  private withCaptcha(token?: string | null) {
    if (!token) return this.withCreds;
    return { ...this.withCreds, headers: { 'x-recaptcha-token': token } };
  }

  /**
   * Step one of signing in: ask how to hash.
   *
   * Returns the salt of the stored hash plus a one-time nonce. The password
   * itself is never sent — see `LoginService.buildProof`.
   */
  loginChallenge(username: string): Observable<LoginChallengeResponse> {
    return this.http.get<LoginChallengeResponse>(
      `${this.baseUrl}/sso/login-challenge`,
      { ...this.withCreds, params: { username } },
    );
  }

  /** Step two: submit the proof derived from the challenge. */
  login(body: {
    username: string;
    challengeId: string;
    proof: string;
    target?: string;
  }): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(
      `${this.baseUrl}/sso/login`,
      body,
      this.withCreds,
    );
  }

  switch(target: string): Observable<SwitchResponse> {
    return this.http.post<SwitchResponse>(
      `${this.baseUrl}/sso/switch`,
      { target },
      this.withCreds,
    );
  }

  logout(): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/sso/logout`,
      {},
      this.withCreds,
    );
  }

  setup(body: { token: string; password: string }): Observable<TokenResult> {
    return this.http.post<TokenResult>(
      `${this.baseUrl}/sso/setup`,
      body,
      this.withCreds,
    );
  }

  /**
   * Ask for a reset link.
   *
   * `ALREADY_REQUESTED` means this caller has asked for this address before
   * and a link is already in the mailbox. It is decided on the asking, not on
   * the account — an invented address gets it on the second try too — so the
   * reply still reveals nothing about who is registered.
   */
  forgot(
    username: string,
    captchaToken?: string | null,
  ): Observable<{ status: 'OK' | 'ALREADY_REQUESTED' }> {
    return this.http.post<{ status: 'OK' | 'ALREADY_REQUESTED' }>(
      `${this.baseUrl}/sso/forgot`,
      { username },
      this.withCaptcha(captchaToken),
    );
  }

  reset(body: { token: string; password: string }): Observable<TokenResult> {
    return this.http.post<TokenResult>(
      `${this.baseUrl}/sso/reset`,
      body,
      this.withCreds,
    );
  }

  unlock(token: string): Observable<TokenResult> {
    return this.http.post<TokenResult>(
      `${this.baseUrl}/sso/unlock`,
      { token },
      this.withCreds,
    );
  }

  myLabs(): Observable<MyLabsResponse> {
    return this.http.get<MyLabsResponse>(
      `${this.baseUrl}/sso/me/labs`,
      this.withCreds,
    );
  }

  // --- Second factor: completing a sign-in ---------------------------------

  /**
   * What the pending challenge offers. The challenge screen asks on load
   * rather than carrying state across the navigation from /login, so a
   * refresh does not strand the user.
   */
  mfaContext(): Observable<MfaContextResponse> {
    return this.http.get<MfaContextResponse>(
      `${this.baseUrl}/sso/mfa/context`,
      this.withCreds,
    );
  }

  mfaVerify(method: MfaMethod, code: string): Observable<MfaVerifyResponse> {
    return this.http.post<MfaVerifyResponse>(
      `${this.baseUrl}/sso/mfa/verify`,
      { method, code },
      this.withCreds,
    );
  }

  /**
   * Ask for an emailed code.
   *
   * Three answers, and the screen says something different for each: a code
   * is coming, one is already in flight and still valid, or this challenge
   * has had all the codes it gets.
   */
  mfaSendEmailOtp(): Observable<MfaSendResult> {
    return this.http.post<MfaSendResult>(
      `${this.baseUrl}/sso/mfa/email-otp/send`,
      {},
      this.withCreds,
    );
  }

  /**
   * Record first-time acceptance of the top-level Terms & Conditions.
   *
   * Called by the T&C dialog on the sign-in page when the login response's
   * `termsAcceptedAt` was null. The sso-be side is idempotent — a second
   * call for the same identity leaves the earlier timestamp untouched —
   * so a double-click or a stale tab reposting the request is safe.
   */
  acceptTerms(): Observable<{ status: 'OK' }> {
    return this.http.post<{ status: 'OK' }>(
      `${this.baseUrl}/sso/me/accept-terms`,
      {},
      this.withCreds,
    );
  }

  // --- Second factor: managing your own settings ---------------------------

  mfaStatus(): Observable<MfaStatusResponse> {
    return this.http.get<MfaStatusResponse>(
      `${this.baseUrl}/sso/me/mfa`,
      this.withCreds,
    );
  }

  mfaTotpStart(): Observable<TotpStartResponse> {
    return this.http.post<TotpStartResponse>(
      `${this.baseUrl}/sso/me/mfa/totp/start`,
      {},
      this.withCreds,
    );
  }

  mfaTotpConfirm(code: string): Observable<EnrolResponse> {
    return this.http.post<EnrolResponse>(
      `${this.baseUrl}/sso/me/mfa/totp/confirm`,
      { code },
      this.withCreds,
    );
  }

  mfaTotpDisable(code: string): Observable<EnrolResponse> {
    return this.http.post<EnrolResponse>(
      `${this.baseUrl}/sso/me/mfa/totp/disable`,
      { code },
      this.withCreds,
    );
  }

  /**
   * Email a code — when enrolling the address, and when confirming a change
   * to the security settings. Same three answers as the sign-in resend, for
   * the same reason: only one of them means mail is actually coming.
   */
  mfaEmailStart(): Observable<MfaSendResult> {
    return this.http.post<MfaSendResult>(
      `${this.baseUrl}/sso/me/mfa/email/start`,
      {},
      this.withCreds,
    );
  }

  mfaEmailConfirm(code: string): Observable<EnrolResponse> {
    return this.http.post<EnrolResponse>(
      `${this.baseUrl}/sso/me/mfa/email/confirm`,
      { code },
      this.withCreds,
    );
  }

  mfaEmailDisable(code: string): Observable<EnrolResponse> {
    return this.http.post<EnrolResponse>(
      `${this.baseUrl}/sso/me/mfa/email/disable`,
      { code },
      this.withCreds,
    );
  }

  mfaRegenerateBackupCodes(code: string): Observable<EnrolResponse> {
    return this.http.post<EnrolResponse>(
      `${this.baseUrl}/sso/me/mfa/backup-codes/regenerate`,
      { code },
      this.withCreds,
    );
  }

  mfaDismissPrompt(): Observable<{ status: 'OK' }> {
    return this.http.post<{ status: 'OK' }>(
      `${this.baseUrl}/sso/me/mfa/dismiss-prompt`,
      {},
      this.withCreds,
    );
  }

  /**
   * Spend the one-shot ticket a Lab put in the redirect, in exchange for a
   * session — so someone who came from their workspace to change their
   * security settings is not asked to sign in a second time.
   */
  redeemSessionTicket(ticket: string): Observable<RedeemSessionTicketResponse> {
    return this.http.post<RedeemSessionTicketResponse>(
      `${this.baseUrl}/sso/session/redeem`,
      { ticket },
      this.withCreds,
    );
  }
}
